import { CommonModule, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy } from '@angular/core';
import { Component } from '@angular/core';
import { BrandLogoHelper } from '../../../../../utils/brand-logo-helper';
import { PreviewBlockHelper } from '../../../../../utils/preview-block-helper';
import {
  GenSingleImagePreviewData
} from '../../../../../models/google-template/preview-interfaces/gen-single-image-preview-data.interface';
import { BasePreviewShellComponent } from '../preview-common/base-preview-shell.component';
import { PreviewVariant } from '../preview-common/preview-variant.interface';
import { GEN_SINGLE_IMAGE_VARIANTS } from './variants/gen-single-image-variants';

/**
 * Demand Gen single-image preview. All the selector mechanics live in
 * {@link BasePreviewShellComponent}; this class only supplies the two
 * type-specific pieces: the variant registry and how to build the flat
 * {@link GenSingleImagePreviewData} DTO from the blocks. Chrome (tabs/toggle/
 * stepper/outlet) is the shared template in `preview-common`.
 *
 * To add a placement, register it in `GEN_VARIANTS`.
 */
@Component({
  selector: 'app-gen-single-image-preview',
  imports: [CommonModule, NgComponentOutlet],
  templateUrl: '../preview-common/base-preview-shell.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../preview-common/base-preview-shell.component.scss'
})
export class GenSingleImagePreviewComponent extends BasePreviewShellComponent<GenSingleImagePreviewData> {
  protected readonly registry: PreviewVariant[] = GEN_SINGLE_IMAGE_VARIANTS;

  protected buildPreviewData(): GenSingleImagePreviewData {
    const url = this.toAbsoluteUrl(
      PreviewBlockHelper.getVar(
        PreviewBlockHelper.findBlock(this.blocks, 'gen-single-image-url'),
        'Link',
      ),
    );
    return {
      headlines: PreviewBlockHelper.collectFromBlock(
        this.blocks,
        'gen-single-image-headline',
        'GenSingleImageHeadline_',
        5,
      ),
      descriptions: PreviewBlockHelper.collectFromBlock(
        this.blocks,
        'gen-single-image-description',
        'GenSingleImageDescription_',
        5,
      ),
      image: PreviewBlockHelper.getVar(
        PreviewBlockHelper.findBlock(this.blocks, 'gen-single-image-image'),
        'GenSingleImageImage_1',
      ),
      images: PreviewBlockHelper.collectFromBlock(
        this.blocks,
        'gen-single-image-image',
        'GenSingleImageImage_',
        20,
      ),
      ctaText: PreviewBlockHelper.getVar(
        PreviewBlockHelper.findBlock(this.blocks, 'gen-single-image-cta-text'),
        'Text',
      ),
      // Only the subdirectory name is stored; resolve it to an asset path once
      // here so all the variants stay presentational.
      logo: BrandLogoHelper.urlFor(
        PreviewBlockHelper.getVar(
          PreviewBlockHelper.findBlock(this.blocks, 'gen-single-image-logo'),
          'Logo',
        ),
      ),
      url,
    };
  }

  /**
   * Prefix a scheme onto a destination URL missing one, e.g. "www.example.com"
   * -> "https://www.example.com". Without this, an `[href]` bound straight to
   * the raw block text resolves as relative to the *editor's own* origin
   * instead of navigating out to the destination.
   */
  private toAbsoluteUrl(rawUrl: string): string {
    const url = rawUrl.trim();
    if (!url) {
      return '';
    }
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  }
}
